import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import api from "@/lib/api";

function formatPlan(plan) {
  if (!plan) return "";
  if (typeof plan === "string") return plan;
  if (typeof plan === "object") return plan?.name || plan?.code || plan?.label || "";
  return String(plan);
}

const LS_BIZ = "current_business_id";

function pickValidBusiness(list) {
  const saved = localStorage.getItem(LS_BIZ);

  // dacă saved e valid în lista curentă, îl folosim
  if (saved) {
    const found = list.find((b) => String(b.id) === String(saved));
    if (found) return found;
  }

  // altfel fallback pe primul business permis
  return list[0] || null;
}

const BusinessSelector = ({ onBusinessChange }) => {
  const { t } = useTranslation();
  const [businesses, setBusinesses] = useState([]);
  const [currentBusiness, setCurrentBusiness] = useState(null);
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  const currentPlanLabel = useMemo(
    () => formatPlan(currentBusiness?.plan),
    [currentBusiness]
  );

  useEffect(() => {
    const fetchBusinesses = async () => {
      try {
        const response = await api.get("/businesses");
        const list = Array.isArray(response.data) ? response.data : [];
        setBusinesses(list);

        const chosen = pickValidBusiness(list);

        setCurrentBusiness(chosen);

        if (chosen) {
          localStorage.setItem(LS_BIZ, String(chosen.id));
          onBusinessChange?.(chosen.id);
        } else {
          localStorage.removeItem(LS_BIZ);
        }

      } catch (error) {
        console.error("Failed to fetch businesses:", error);
      }
    };

    fetchBusinesses();
  }, [onBusinessChange]);

  useEffect(() => {
    if (!open) return;

    const onDown = (e) => {
      if (!wrapRef.current) return;
      if (wrapRef.current.contains(e.target)) return;
      setOpen(false);
    };

    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };

    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const handleBusinessSelect = (business) => {
    setCurrentBusiness(business);
    onBusinessChange?.(business.id);
    localStorage.setItem("current_business_id", business.id);
    setOpen(false);
  };

  if (!currentBusiness) return null;

  return (
    <div className="relative" ref={wrapRef}>
      {/* Trigger (afișează o singură dată numele + planul) */}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full text-left px-3 py-2 rounded-md border border-border bg-background hover:bg-muted transition-colors"
      >
        <div className="flex flex-col">
          <span className="text-sm font-medium text-foreground">
            {currentBusiness.name}
          </span>
          <span className="text-xs text-muted-foreground mt-1">
            {currentPlanLabel || "—"}
          </span>
        </div>
      </button>

      {/* Dropdown list (nu mai repetăm planul aici) */}
      {open && (
        <div className="absolute right-0 mt-2 w-[320px] max-w-[92vw] bg-popover border border-border rounded-lg shadow-elevation-2 overflow-hidden z-50">
          <div className="max-h-[320px] overflow-auto py-1">
            {businesses.map((business) => {
              const isActive = String(business.id) === String(currentBusiness.id);
              return (
                <button
                  key={business.id}
                  type="button"
                  onClick={() => handleBusinessSelect(business)}
                  className={`w-full text-left px-3 py-2 text-sm transition-colors ${isActive ? "bg-primary text-primary-foreground" : "hover:bg-muted"
                    }`}
                >
                  <div className="flex flex-col">
                    <span className="font-medium">{business.name}</span>
                    <span className={`text-xs ${isActive ? "opacity-90" : "text-muted-foreground"}`}>
                      {t("admin.appointmentsCount", { count: business?.appointmentsCount ?? 0 })}
                    </span>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default BusinessSelector;
